import { useEffect, useMemo, useRef } from 'react';
import { TagOption } from '@tw/tw-component-library';
import { UseAuth } from '../../../../../contexts/AuthContext';
import { useFormContext } from '../../../../../contexts/useFormContext';
import { toTagOptions } from '../../../../../utils/transformers';
import { FormState } from '../../types';
import { isGiftHospitalityWorkflow } from './authoriserRules';
import { buildCreatorPersonTag } from './givenReceivedByPerson';

export const useCreatorPersonTag = (formState: FormState): TagOption | null => {
  const { currentUser } = UseAuth();

  return useMemo(() => {
    if (!isGiftHospitalityWorkflow(formState.workflowType)) {
      return null;
    }

    return buildCreatorPersonTag({
      createdBy: formState.data?.createdBy,
      createdByName: formState.data?.createdByName,
      createdByEmail: formState.data?.createdByEmail,
      currentUser,
    });
  }, [
    currentUser,
    formState.data?.createdBy,
    formState.data?.createdByEmail,
    formState.data?.createdByName,
    formState.workflowType,
  ]);
};

export const useSeedCreatorPersonTag = (
  formState: FormState,
  creatorTag: TagOption | null
): void => {
  const { updateFormData } = useFormContext();
  const seededWorkflowIdRef = useRef<string | null>(null);
  const workflowId = String(formState.data?.id ?? '');
  const personValue = formState.data?.givenReceivedByPerson;
  const workflowType = formState.workflowType;

  useEffect(() => {
    if (!isGiftHospitalityWorkflow(workflowType)) {
      return;
    }

    if (seededWorkflowIdRef.current === workflowId) {
      return;
    }

    if (toTagOptions(personValue).length > 0) {
      seededWorkflowIdRef.current = workflowId;
      return;
    }

    if (personValue !== undefined || !creatorTag) {
      return;
    }

    seededWorkflowIdRef.current = workflowId;
    updateFormData({
      givenReceivedByPerson: [creatorTag],
    });
  }, [creatorTag, personValue, updateFormData, workflowId, workflowType]);
};
